<svelte:options customElement="hb-input-date" />

<script lang="ts">
  /**
   * Svelte Input Date WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import type { Component } from "./types/webcomponent.type";
  import {
    parseSchemaentryProp,
    sameSetValPayload,
    schemaentryFingerprint,
    shouldApplySchemaSync,
  } from "../lib/schemaentry";

  let { schemaentry, show_validation = "no" }: Component = $props();

  let field = $state<Record<string, any> | null>(null);
  let schemaFingerprint = $state("");

  let value: string | undefined = $state(undefined);
  let value_date: Date | undefined = $derived(
    value ? new Date(value) : undefined,
  );

  let valid = $derived.by(() => {
    if (field && field.required) {
      if (value) {
        if (
          min_date &&
          value_date &&
          !(value_date.valueOf() >= min_date.valueOf())
        ) {
          return false;
        } else if (
          max_date &&
          value_date &&
          !(value_date.valueOf() <= max_date.valueOf())
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    }
    return true;
  });

  let min_date: Date | undefined = $derived(
    field?.params?.min ? new Date(field.params.min) : undefined,
  );
  let max_date: Date | undefined = $derived(
    field?.params?.max ? new Date(field.params.max) : undefined,
  );

  let minAttr = $derived(
    min_date && !Number.isNaN(min_date.valueOf())
      ? min_date.toISOString().slice(0, 10)
      : undefined,
  );
  let maxAttr = $derived(
    max_date && !Number.isNaN(max_date.valueOf())
      ? max_date.toISOString().slice(0, 10)
      : undefined,
  );

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  $effect(() => {
    const fp = schemaentryFingerprint(schemaentry);
    const nextField = parseSchemaentryProp(schemaentry);
    if (!nextField) {
      field = null;
      return;
    }
    if (shouldApplySchemaSync(fp, () => schemaFingerprint)) {
      schemaFingerprint = fp;
      field = nextField;
      const fromSchema = nextField.value as string | undefined;
      value = fromSchema != null ? String(fromSchema) : undefined;
    }
  });

  let lastSetVal = $state<{
    value: unknown;
    valid: boolean;
    id: string | undefined;
  } | null>(null);

  $effect(() => {
    const f = field;
    if (!f?.id) return;
    const payload = { value, valid, id: f.id as string | undefined };
    if (sameSetValPayload(lastSetVal, payload)) return;
    lastSetVal = payload;
    dispatch("setVal", { value, valid, id: f.id });
  });
</script>

{#if field != null}
  <div class="field">
    <div class="control">
      <input
        bind:value
        type="date"
        class="input"
        class:is-success={show_validation === "yes" &&
          !!field?.required &&
          valid}
        class:is-danger={show_validation === "yes" &&
          !!field?.required &&
          !valid}
        id={field?.id}
        required={field?.required}
        placeholder={field?.placeholder}
        readonly={field?.readonly}
        disabled={!!field?.disabled}
        min={minAttr}
        max={maxAttr}
        onkeypress={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            dispatch("clickEnter", { value, valid, id: field?.id });
          }
        }}
      />
    </div>
    {#if field?.validationTip && show_validation === "yes" && !valid}
      <p part="invalid-feedback" class="help is-danger">
        {field.validationTip}
      </p>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
